import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Check,
  Mail,
  Lock,
  Server,
  AlertCircle,
  Loader2,
  Palette,
  Globe,
  ShieldCheck,
  Settings,
  Eye,
  Info,
} from "lucide-react";
import { useAuth, getAuthToken } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import useCompany from "../../api/company";
import ImageDisplayer from "./ImageDisplayer";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:9000/";
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL;

const getDisplayUrl = (id) => {
  if (!id) return null;
  if (
    typeof id === "string" &&
    (id.startsWith("http") ||
      id.startsWith("data:image/") ||
      id.startsWith("blob:"))
  ) {
    return id;
  }
  if (typeof id === "string" && id.startsWith("images/")) {
    return `${S3_BASE_URL}/${id}`;
  }
  return null;
};

const SMTPSettingsDialog = ({ open, onOpenChange }) => {
  const { company, updateCompanyContext } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { updateCompany } = useCompany();

  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState("server");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [showAliasHelp, setShowAliasHelp] = useState(false);

  const [formData, setFormData] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_pass: "",
    smtp_secure: false,
    sender_email: "", // Alias email to send from (optional)
    primary_color: "#1E4FCF",
    logo_url: "",
    company_name: "",
    website_url: "",
    email_signature: "",
  });

  useEffect(() => {
    if (company) {
      let settings = {};
      if (company.email_settings) {
        if (typeof company.email_settings === "string") {
          try {
            settings = JSON.parse(company.email_settings);
          } catch (e) {
            console.error("Error parsing email settings", e);
          }
        } else {
          settings = company.email_settings;
        }
      }

      setFormData({
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port || "587",
        smtp_user: settings.smtp_user || "",
        smtp_pass: settings.smtp_pass ? "********" : "",
        smtp_secure: settings.smtp_secure || false,
        sender_email: settings.sender_email || "",
        logo_url: settings.logo_url || "",
        primary_color: settings.primary_color || "#1E4FCF",
        company_name: settings.company_name || company.name || "",
        website_url: settings.website_url || "",
        email_signature: settings.email_signature || "",
      });
      setPasswordChanged(false);
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "smtp_pass") {
      setPasswordChanged(true);
    }
    if (testResult) setTestResult(null);
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, smtp_secure: checked }));
    if (testResult) setTestResult(null);
  };

  const performTestConnection = async () => {
    const token = getAuthToken();
    const payload = {
      smtp_host: formData.smtp_host,
      smtp_port: parseInt(formData.smtp_port),
      smtp_user: formData.smtp_user,
      smtp_pass: formData.smtp_pass,
      smtp_secure: formData.smtp_secure,
      company_id: company.id,
    };

    return await axios.post(
      `${API_BASE_URL}api/mails/test-connection`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      await performTestConnection();
      setTestResult({
        type: "success",
        message:
          "Verbinding geslaagd! Je SMTP server is correct geconfigureerd.",
      });
    } catch (err) {
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message ||
        "Verbinding mislukt";
      setTestResult({ type: "error", message: msg });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        email_settings: {
          smtp_host: formData.smtp_host,
          smtp_port: formData.smtp_port,
          smtp_user: formData.smtp_user,
          smtp_secure: formData.smtp_secure,
          sender_email: formData.sender_email || null, // Alias to send from
          ...(passwordChanged && { smtp_pass: formData.smtp_pass }),
          logo_url: formData.logo_url,
          primary_color: formData.primary_color,
          company_name: formData.company_name,
          website_url: formData.website_url,
          email_signature: formData.email_signature,
        },
      };

      const updatedCompany = await updateCompany(company.id, payload);
      updateCompanyContext(updatedCompany);
      setSuccess("Instellingen succesvol opgeslagen.");
      setTimeout(() => {
        onOpenChange(false);
        setSuccess("");
      }, 1500);
    } catch (err) {
      setError("Er is een fout opgetreden bij het opslaan.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-6xl w-full max-h-[90vh] p-0 overflow-hidden border-none shadow-2xl rounded-xl flex flex-col ${isDark ? "bg-[#141414]" : "bg-[#f8f8fa]"}`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 flex items-center justify-between border-b shrink-0 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
            >
              <Settings
                className={`h-4 w-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}
              />
            </div>
            <div>
              <h2
                className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                E-mail Instellingen
              </h2>
              <p
                className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
              >
                Configureer je SMTP server en huisstijl
              </p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {(error || success) && (
          <div className="px-6 pt-4">
            {error && (
              <div
                className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-red-500/8 border border-red-500/15 text-red-300" : "bg-red-50 border border-red-100 text-red-700"}`}
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div
                className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-[13px] ${isDark ? "bg-[#BFFF00]/8 border border-[#BFFF00]/15 text-[#BFFF00]" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}
              >
                <Check className="h-4 w-4 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="h-full flex flex-col"
          >
            <div
              className={`px-6 pt-4 border-b shrink-0 ${isDark ? "bg-[#141414] border-white/[0.04]" : "bg-white border-gray-100"}`}
            >
              <TabsList
                className={`grid w-full max-w-md grid-cols-2 p-0.5 rounded-lg ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
              >
                <TabsTrigger
                  value="server"
                  className={`rounded-md text-[12px] font-medium data-[state=active]:shadow-sm ${isDark ? "data-[state=active]:bg-white/[0.1] data-[state=active]:text-white text-gray-500" : "data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-500"}`}
                >
                  <Server className="h-3.5 w-3.5 mr-1.5" />
                  Server
                </TabsTrigger>
                <TabsTrigger
                  value="branding"
                  className={`rounded-md text-[12px] font-medium data-[state=active]:shadow-sm ${isDark ? "data-[state=active]:bg-white/[0.1] data-[state=active]:text-white text-gray-500" : "data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-500"}`}
                >
                  <Palette className="h-3.5 w-3.5 mr-1.5" />
                  Huisstijl
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Server Tab */}
              <TabsContent value="server" className="mt-0 space-y-6">
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Main Settings */}
                  <div className="lg:col-span-2 space-y-6">
                    <div
                      className={`rounded-xl p-5 space-y-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div>
                        <h3
                          className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          SMTP Configuratie
                        </h3>
                        <p
                          className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Vul de gegevens van je mailserver in
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            SMTP Host
                          </Label>
                          <div className="relative">
                            <Globe
                              className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            />
                            <Input
                              name="smtp_host"
                              value={formData.smtp_host}
                              onChange={handleChange}
                              placeholder="smtp.office365.com"
                              className={`pl-9 h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label
                              className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Poort
                            </Label>
                            <Input
                              name="smtp_port"
                              value={formData.smtp_port}
                              onChange={handleChange}
                              placeholder="587"
                              className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label
                              className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              SSL/TLS
                            </Label>
                            <div
                              className={`h-9 px-3 rounded-lg flex items-center justify-between ${isDark ? "bg-white/[0.04] border border-white/[0.06]" : "bg-gray-50 border border-gray-200"}`}
                            >
                              <span
                                className={`text-[12px] ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                Beveiligd
                              </span>
                              <Switch
                                checked={formData.smtp_secure}
                                onCheckedChange={handleSwitchChange}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`h-px ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                      />

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Gebruikersnaam / Email
                          </Label>
                          <div className="relative">
                            <Mail
                              className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            />
                            <Input
                              name="smtp_user"
                              value={formData.smtp_user}
                              onChange={handleChange}
                              placeholder="noreply@bedrijf.be"
                              className={`pl-9 h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Wachtwoord
                          </Label>
                          <div className="relative">
                            <Lock
                              className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            />
                            <Input
                              name="smtp_pass"
                              type="password"
                              value={formData.smtp_pass}
                              onChange={handleChange}
                              placeholder="••••••••"
                              className={`pl-9 h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sender Alias Email */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Verzend Alias (optioneel)
                          </Label>
                          <button
                            type="button"
                            onClick={() => setShowAliasHelp(!showAliasHelp)}
                            className={`flex items-center gap-1 text-[11px] transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                          >
                            <Info className="h-3 w-3" />
                            <span>
                              {showAliasHelp ? "Verberg" : "Hoe werkt dit?"}
                            </span>
                          </button>
                        </div>
                        <div className="relative">
                          <Mail
                            className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          />
                          <Input
                            name="sender_email"
                            value={formData.sender_email}
                            onChange={handleChange}
                            placeholder="noreply@bedrijf.be"
                            className={`pl-9 h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                          />
                        </div>
                        <p
                          className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          Optioneel: verstuur emails vanaf dit adres. Laat leeg
                          om de SMTP gebruiker te gebruiken.
                        </p>
                        {showAliasHelp && (
                          <div
                            className={`mt-2 p-4 rounded-lg space-y-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
                          >
                            <div className="space-y-2">
                              <p
                                className={`font-medium text-[12px] flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}
                              >
                                <span className="w-5 h-5 bg-red-500 rounded text-white text-[10px] flex items-center justify-center font-bold">
                                  G
                                </span>
                                Gmail / Google Workspace
                              </p>
                              <ol
                                className={`list-decimal list-inside text-[11px] space-y-0.5 ml-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                <li>Log in op je Gmail hoofdaccount</li>
                                <li>
                                  Ga naar <strong>Instellingen</strong> →{" "}
                                  <strong>Alle instellingen bekijken</strong>
                                </li>
                                <li>
                                  Open het tabblad <strong>Accounts</strong>
                                </li>
                                <li>
                                  Bij <strong>"Mail verzenden als"</strong>,
                                  klik op{" "}
                                  <strong>Nog een e-mailadres toevoegen</strong>
                                </li>
                                <li>
                                  Vul je alias in en vink{" "}
                                  <strong>"Beschouwen als alias"</strong> aan
                                </li>
                              </ol>
                            </div>
                            <div
                              className={`border-t ${isDark ? "border-white/[0.04]" : "border-gray-200"}`}
                            />
                            <div className="space-y-2">
                              <p
                                className={`font-medium text-[12px] flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}
                              >
                                <span className="w-5 h-5 bg-blue-600 rounded text-white text-[10px] flex items-center justify-center font-bold">
                                  M
                                </span>
                                Microsoft 365 / Outlook
                              </p>
                              <ol
                                className={`list-decimal list-inside text-[11px] space-y-0.5 ml-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                              >
                                <li>
                                  Log in op het{" "}
                                  <strong>Microsoft 365 Admin Center</strong>
                                </li>
                                <li>
                                  Ga naar <strong>Gebruikers</strong> →{" "}
                                  <strong>Actieve gebruikers</strong>
                                </li>
                                <li>
                                  Selecteer de gebruiker en klik op{" "}
                                  <strong>E-mail</strong>
                                </li>
                                <li>
                                  Klik op{" "}
                                  <strong>E-mailaliassen beheren</strong>
                                </li>
                                <li>
                                  Voeg je alias toe (bijv. noreply@bedrijf.be)
                                </li>
                              </ol>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Test Connection */}
                      <div
                        className={`pt-4 border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                      >
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleTestConnection}
                          disabled={
                            isTesting || isLoading || !formData.smtp_host
                          }
                          className={`w-full h-9 rounded-lg text-[12px] font-medium border-dashed transition-all ${isDark ? "border-white/[0.08] text-gray-400 hover:border-white/20 hover:text-gray-200 hover:bg-white/[0.04]" : "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50"}`}
                        >
                          {isTesting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                          ) : (
                            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Test Verbinding
                        </Button>

                        {testResult && (
                          <div
                            className={`mt-3 px-3 py-2.5 rounded-lg text-[12px] font-medium flex items-start gap-2 ${
                              testResult.type === "success"
                                ? isDark
                                  ? "bg-[#BFFF00]/8 border border-[#BFFF00]/15 text-[#BFFF00]"
                                  : "bg-emerald-50 border border-emerald-100 text-emerald-700"
                                : isDark
                                  ? "bg-red-500/8 border border-red-500/15 text-red-300"
                                  : "bg-red-50 border border-red-100 text-red-700"
                            }`}
                          >
                            {testResult.type === "success" ? (
                              <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            )}
                            <span>{testResult.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Info Sidebar */}
                  <div className="space-y-4">
                    <div
                      className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div className="flex items-center gap-2.5 mb-3">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${testResult?.type === "success" ? "bg-emerald-400 animate-pulse" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                        />
                        <span
                          className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {testResult?.type === "success"
                            ? "Verbonden"
                            : "Niet verbonden"}
                        </span>
                      </div>
                      <p
                        className={`text-[12px] leading-relaxed ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        {testResult?.type === "success"
                          ? "Je SMTP server is correct geconfigureerd."
                          : "Test de verbinding om je instellingen te controleren."}
                      </p>
                    </div>

                    <div
                      className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <h4
                        className={`text-[12px] font-semibold mb-3 ${isDark ? "text-gray-200" : "text-gray-900"}`}
                      >
                        Populaire Providers
                      </h4>
                      <div className="space-y-0">
                        {[
                          {
                            name: "Microsoft 365",
                            host: "smtp.office365.com:587",
                          },
                          { name: "Gmail", host: "smtp.gmail.com:587" },
                          { name: "Combell", host: "mail.combell.com:587" },
                        ].map((p, i) => (
                          <div
                            key={p.name}
                            className={`flex justify-between py-2 text-[11px] ${i < 2 ? (isDark ? "border-b border-white/[0.04]" : "border-b border-gray-100") : ""}`}
                          >
                            <span
                              className={
                                isDark ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              {p.name}
                            </span>
                            <span
                              className={`font-mono ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            >
                              {p.host}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Branding Tab */}
              <TabsContent value="branding" className="mt-0 space-y-6">
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Main Branding Settings */}
                  <div className="lg:col-span-2 space-y-6">
                    <div
                      className={`rounded-xl p-5 space-y-5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div>
                        <h3
                          className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          Visuele Identiteit
                        </h3>
                        <p
                          className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Pas het uiterlijk van je emails aan
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Bedrijfsnaam
                          </Label>
                          <Input
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            placeholder="Jouw Bedrijf"
                            className={`h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                          />
                          <p
                            className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                          >
                            Wordt weergegeven in de footer
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <Label
                            className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Website URL
                          </Label>
                          <div className="relative">
                            <Globe
                              className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                            />
                            <Input
                              name="website_url"
                              value={formData.website_url}
                              onChange={handleChange}
                              placeholder="www.jouwbedrijf.be"
                              className={`pl-9 h-9 rounded-lg text-[13px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600 focus:border-white/15" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-gray-300"}`}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label
                          className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Accent Kleur
                        </Label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            name="primary_color"
                            value={formData.primary_color || "#1E4FCF"}
                            onChange={handleChange}
                            className={`w-9 h-9 rounded-lg border cursor-pointer bg-transparent p-0.5 ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                          />
                          <Input
                            name="primary_color"
                            value={formData.primary_color || "#1E4FCF"}
                            onChange={handleChange}
                            className={`w-28 h-9 font-mono uppercase rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                          />
                          <div className="flex gap-1.5">
                            {[
                              "#1E4FCF",
                              "#10B981",
                              "#F59E0B",
                              "#EF4444",
                              "#8B5CF6",
                            ].map((color) => (
                              <button
                                key={color}
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    primary_color: color,
                                  }))
                                }
                                className={`w-7 h-7 rounded-lg transition-all hover:scale-110 ${formData.primary_color === color ? "ring-2 ring-offset-2 ring-gray-400" : ""}`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      <div
                        className={`h-px ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                      />

                      <div className="space-y-1.5">
                        <Label
                          className={`text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Email Logo
                        </Label>
                        <div
                          className={`border border-dashed rounded-xl p-5 transition-all ${isDark ? "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/15" : "border-gray-200 bg-gray-50/50 hover:bg-white hover:border-gray-300"}`}
                        >
                          <ImageDisplayer
                            imageId={formData.logo_url}
                            onChange={(newUrl) =>
                              setFormData((prev) => ({
                                ...prev,
                                logo_url: newUrl,
                              }))
                            }
                            className="w-full"
                          />
                        </div>
                        <p
                          className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          Upload je logo (wordt automatisch verkleind, aanbevolen: 200x60px)
                        </p>
                      </div>
                    </div>

                    <div
                      className={`rounded-xl p-5 space-y-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div>
                        <h3
                          className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          Email Handtekening
                        </h3>
                        <p
                          className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          Voeg een handtekening toe aan uitgaande emails
                        </p>
                      </div>
                      <div
                        className={`rounded-lg overflow-hidden border ${isDark ? "border-white/[0.06] smtp-quill-dark" : "border-gray-200"}`}
                      >
                        <ReactQuill
                          theme="snow"
                          value={formData.email_signature}
                          onChange={(val) =>
                            setFormData((prev) => ({
                              ...prev,
                              email_signature: val,
                            }))
                          }
                          className="smtp-quill"
                          placeholder="Met vriendelijke groeten,&#10;Jan Janssens"
                          modules={{
                            toolbar: [
                              ["bold", "italic", "underline"],
                              ["link"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["clean"],
                            ],
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="hidden lg:block">
                    <div
                      className={`rounded-xl sticky top-0 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                    >
                      <div
                        className={`px-4 py-3 border-b ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                      >
                        <p
                          className={`text-[11px] font-medium uppercase tracking-wider flex items-center gap-1.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Live Voorbeeld
                        </p>
                      </div>
                      <div className="p-4">
                        {/* Email Preview — always light since it's an actual email */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center justify-center bg-gray-50">
                            {getDisplayUrl(formData.logo_url) ? (
                              <img
                                src={getDisplayUrl(formData.logo_url)}
                                alt="Logo"
                                className="h-8 w-auto object-contain max-w-[150px]"
                              />
                            ) : (
                              <span
                                className="text-base font-bold"
                                style={{
                                  color: formData.primary_color || "#1E4FCF",
                                }}
                              >
                                {formData.company_name || "Jouw Logo"}
                              </span>
                            )}
                          </div>
                          <div className="p-4 space-y-3">
                            <div
                              className="h-1 w-10 rounded-full"
                              style={{
                                backgroundColor:
                                  formData.primary_color || "#1E4FCF",
                              }}
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Onderwerp Email
                            </h4>
                            <div className="space-y-1.5">
                              <div className="h-1.5 bg-gray-100 rounded-full w-full" />
                              <div className="h-1.5 bg-gray-100 rounded-full w-5/6" />
                              <div className="h-1.5 bg-gray-100 rounded-full w-4/6" />
                            </div>
                            <button
                              className="w-full py-2 rounded-lg text-[12px] font-medium text-white"
                              style={{
                                backgroundColor:
                                  formData.primary_color || "#1E4FCF",
                              }}
                            >
                              Actie Knop
                            </button>
                            {formData.email_signature && (
                              <div className="pt-3 border-t border-gray-100">
                                <div
                                  className="text-[10px] text-gray-500 line-clamp-4 signature-preview"
                                  dangerouslySetInnerHTML={{
                                    __html: formData.email_signature,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="p-3 bg-gray-50 border-t border-gray-100 text-center">
                            {formData.website_url && (
                              <span
                                className="text-[10px] font-medium"
                                style={{
                                  color: formData.primary_color || "#1E4FCF",
                                }}
                              >
                                {formData.website_url.replace(
                                  /^https?:\/\//,
                                  "",
                                )}
                              </span>
                            )}
                            <p className="text-[9px] text-gray-400 mt-0.5">
                              © 2026 {formData.company_name || company?.name}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-2 shrink-0 ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200"}`}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className={`rounded-lg text-[12px] ${isDark ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            Annuleren
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isLoading}
            className={`rounded-lg text-[12px] font-medium ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <Check className="h-3.5 w-3.5 mr-1.5" />
            )}
            Opslaan
          </Button>
        </div>
      </DialogContent>

      <style jsx="true">{`
        .smtp-quill .ql-toolbar.ql-snow {
          border: none !important;
          border-bottom: 1px solid #e5e7eb !important;
          background: #f9fafb;
          padding: 8px 12px !important;
        }
        .smtp-quill .ql-container.ql-snow {
          border: none !important;
          min-height: 120px;
        }
        .smtp-quill .ql-editor {
          padding: 16px !important;
          font-size: 14px;
          line-height: 1.6;
        }
        .smtp-quill .ql-editor.ql-blank::before {
          font-style: normal;
          color: #9ca3af;
        }
        .signature-preview * {
          margin: 0 !important;
          padding: 0 !important;
          font-family: inherit !important;
        }
        .signature-preview p {
          margin-bottom: 4px !important;
        }
      `}</style>
    </Dialog>
  );
};

export default SMTPSettingsDialog;
